/**
 * Support cancel affordance for one bulk RMA action request: status text, errors, and confirm flow.
 */
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { Button } from '../../../../../../components/inputs/button/button';
import { BUTTON_VARIANTS } from '../../../../../../components/inputs/button-variants';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../../components/fonts/font-props';
import FontBody2 from '../../../../../../components/fonts/font-body2/font-body2';
import { ConfirmModal } from '../../../../../../components/modal/components/confirm-modal/confirm-modal';

import bulkRmaActionRequestDetailStore from '../../stores/bulk-rma-action-request-detail-store';

import './request-cancel-controls.scss';

const RequestCancelControls: React.FC = observer(() => {
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const {
        canCancelRequest,
        isCancellationInProgress,
        isCancelling,
        cancelError,
        isOpen,
    } = bulkRmaActionRequestDetailStore;

    useEffect(() => {
        if (!isOpen) {
            setShowCancelConfirm(false);
        }
    }, [isOpen]);

    const confirmCancelRequest = async () => {
        setShowCancelConfirm(false);
        await bulkRmaActionRequestDetailStore.cancelRequest();
    };

    if (!isCancellationInProgress && !canCancelRequest && !cancelError) {
        return null;
    }

    return (
        <>
            <div className="bulk-rma-request-cancel-controls">
                {isCancellationInProgress && (
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        Cancellation in progress…
                    </FontBody2>
                )}
                {cancelError && (
                    <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                        {cancelError}
                    </FontBody2>
                )}
                {canCancelRequest && (
                    <Button
                        text="Cancel request"
                        variant={BUTTON_VARIANTS.Secondary}
                        hasLoader
                        isLoading={isCancelling}
                        disabled={isCancelling}
                        onClick={() => {
                            bulkRmaActionRequestDetailStore.clearCancelError();
                            setShowCancelConfirm(true);
                        }}
                    />
                )}
            </div>
            {showCancelConfirm && (
                <ConfirmModal
                    title="Cancel bulk request"
                    text=""
                    implicationText={(
                        <>
                            Queued work will stop. Returns already being processed may still
                            finish. This unblocks the merchant from starting another bulk
                            request.
                        </>
                    )}
                    questionText="Are you sure you want to cancel this bulk request?"
                    cancelButton={{
                        title: 'Keep running',
                        onClick: () => setShowCancelConfirm(false),
                    }}
                    okButton={{
                        title: 'Cancel request',
                        onClick: confirmCancelRequest,
                    }}
                />
            )}
        </>
    );
});

export default RequestCancelControls;
